import { gridTotal, isCurved, isFolded, } from '@signa/shared';
import { curvedLayout, curvedOutline, largestPolygon } from './curve.js';
import { panelSize } from './fit.js';
import { apply, computeHomography, DegenerateQuadError, invert, } from './homography.js';
import { toStage } from './layout.js';
/** 外積。2 点を通る直線・2 直線の交点のどちらも、同次座標ではこれ 1 つで求まる。 */
function cross(a, b) {
    return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
}
function pointOf(p) {
    return [p[0], p[1], 1];
}
/** 2 点を通る直線。 */
function lineThrough(a, b) {
    return cross(pointOf(a), pointOf(b));
}
/** 外側の辺。**`quad` は左上から時計回り**(F-08)。 */
function axisEdges(quad, axis) {
    const [tl, tr, br, bl] = quad;
    if (tl === undefined || tr === undefined || br === undefined || bl === undefined)
        return null;
    const minus = (a, b) => [a[0] - b[0], a[1] - b[1]];
    return axis === 'columns'
        ? // 縦の折れ目: 左辺 → 右辺。上の端点は上辺(左上 → 右上)、下の端点は下辺(左下 → 右下)の向きにずらす
            { start: [tl, bl], end: [tr, br], slides: [minus(tr, tl), minus(br, bl)] }
        : // 横の折れ目: 上辺 → 下辺。左の端点は左辺(左上 → 左下)、右の端点は右辺(右上 → 右下)の向きにずらす
            { start: [tl, tr], end: [bl, br], slides: [minus(bl, tl), minus(br, tr)] };
}
/**
 * **継ぎ目をそろえる**(ADR-0057 の決定 3 と訂正)。動かした境目の両端を、そろえた位置で返す(番号は入力のまま)。
 *
 * ## そろえ方
 *
 * 1. 外側の 2 辺(縦の折れ目なら左辺と右辺)の直線の交点 V を求める。**展開すると長方形の盤面では、
 *    外側の辺と折れ目は 3D ですべて平行**なので、写真の上では V に集まるのが正しい
 * 2. 境目の中点と V を結ぶ線 ℓ を引く
 * 3. 境目の両端を、**その端点が乗る外側の辺の向きに沿って** ℓ までずらす
 *
 * これで両側の面のホモグラフィが**継ぎ目の上で同じ割り振り**になり、素材が継ぎ目で段にならない
 * (しないと、細く写る面で置き方の誤差が 5〜12 倍の段になる —— [PoC](../../../poc/d327-seam-alignment/FINDINGS.md))。
 *
 * ## 決まらないとき
 *
 * - **V が決まらない**(外側の 2 辺が同じ直線 —— 枠が潰れている)—— そろえずに返す
 * - **V が無限遠**(2 辺が平行)—— 同次座標なので**同じ式で**「中点を通り V の向きの線」になる
 * - **ずらす先が決まらない**(ℓ とずらす向きが平行)—— その端点はそのまま
 *
 * **保存値は変えない。** 描くたび・判定するたびにここを通す(4 隅を動かすと V が変わるため)。
 */
export function alignFolds(quad, folds) {
    const edges = axisEdges(quad, folds.axis);
    if (edges === null)
        return folds.lines.map((line) => line.points);
    const vanishing = cross(lineThrough(edges.start[0], edges.start[1]), lineThrough(edges.end[0], edges.end[1]));
    if (!isUsable(vanishing))
        return folds.lines.map((line) => line.points);
    return folds.lines.map(({ points: [first, second] }) => {
        const middle = [(first[0] + second[0]) / 2, (first[1] + second[1]) / 2];
        const towardVanishing = cross(pointOf(middle), vanishing);
        return [
            slideOnto(first, edges.slides[0], towardVanishing),
            slideOnto(second, edges.slides[1], towardVanishing),
        ];
    });
}
/** 同次座標が 1 つの点・直線を表しているか(全成分が 0 なら不定)。 */
function isUsable(value) {
    return value.every(Number.isFinite) && value.some((component) => component !== 0);
}
/** `point` を `direction` の向きに沿って直線 `line` までずらす。決まらなければ `point` のまま。 */
function slideOnto(point, direction, line) {
    // `point` を通り `direction` の向きの直線(向きは無限遠点 `[dx, dy, 0]`)。
    const along = cross(pointOf(point), [direction[0], direction[1], 0]);
    const [x, y, w] = cross(line, along);
    // **ほぼ平行なら交点が遠すぎる**(w が x・y に比べて小さすぎる)—— 動かさない。
    if (!Number.isFinite(w) || Math.abs(w) <= 1e-12 * Math.max(Math.abs(x), Math.abs(y))) {
        return point;
    }
    return [x / w, y / w];
}
/** 境目の位置(累積和)。**項が n 個なら n + 1 個。** `@signa/shared` の `gridCells` と同じ手順で作る。 */
function edgesOf(terms) {
    const edges = [0];
    let sum = 0;
    for (const term of terms) {
        sum += term;
        edges.push(sum);
    }
    return edges;
}
/**
 * 折れた枠の割り付け(ADR-0057 の決定 2)。
 *
 * `folds` の境目は**動かした境目**で、ここでそろえる([`alignFolds`](#alignFolds))。
 * 番号が範囲外の境目は無視する(業務 API が弾くので保存された値には無い)。
 *
 * 描けないときは `null` —— 分割が無い・折る向きの項が 1 つ・枠が潰れて展開図の寸法が決まらない。
 * **面が潰れているだけなら `null` にしない**(その面は描く側が飛ばす —— 同意書の仮定 F)。
 */
export function foldedLayout(quad, grid, folds) {
    if (grid == null)
        return null;
    const terms = grid[folds.axis];
    if (terms.length < 2)
        return null;
    const edges = axisEdges(quad, folds.axis);
    if (edges === null)
        return null;
    const total = gridTotal(grid);
    const panel = panelSize(quad, total);
    if (!(panel.width > 0 && panel.height > 0))
        return null;
    // 境目の位置を展開図の座標に開く。**`frameLayout` と同じく、累積和に倍率を掛けるだけ。**
    const length = folds.axis === 'columns' ? panel.width : panel.height;
    const scale = length / (folds.axis === 'columns' ? total.width : total.height);
    const positions = edgesOf(terms).map((edge) => edge * scale);
    const aligned = alignFolds(quad, folds);
    const moved = new Map();
    folds.lines.forEach((line, i) => {
        const ends = aligned[i];
        if (ends !== undefined && line.index >= 0 && line.index < terms.length - 1) {
            moved.set(line.index, ends);
        }
    });
    const anchors = [
        { position: 0, ends: edges.start },
        ...[...moved.entries()]
            .sort(([a], [b]) => a - b)
            .map(([index, ends]) => ({ position: positions[index + 1] ?? 0, ends })),
        { position: length, ends: edges.end },
    ];
    const faces = [];
    for (let i = 0; i + 1 < anchors.length; i += 1) {
        const a = anchors[i];
        const b = anchors[i + 1];
        if (a === undefined || b === undefined)
            continue;
        faces.push(faceBetween(a, b, folds.axis, panel));
    }
    const boundaries = terms.slice(1).map((_, index) => {
        const fixed = moved.get(index);
        if (fixed !== undefined)
            return fixed;
        return projectBoundary(positions[index + 1] ?? 0, anchors, faces, folds.axis, panel);
    });
    return { panel, faces, boundaries, outline: outlineOf(anchors, folds.axis) };
}
/** 2 つの区切りのあいだの面。**4 隅は区切りの点をそのまま使う**(写像で求めない)。 */
function faceBetween(a, b, axis, panel) {
    return axis === 'columns'
        ? {
            panelCorners: [
                [a.position, 0],
                [b.position, 0],
                [b.position, panel.height],
                [a.position, panel.height],
            ],
            // 区切りは `[上, 下]`。左上 → 右上 → 右下 → 左下。
            corners: [a.ends[0], b.ends[0], b.ends[1], a.ends[1]],
        }
        : {
            panelCorners: [
                [0, a.position],
                [panel.width, a.position],
                [panel.width, b.position],
                [0, b.position],
            ],
            // 区切りは `[左, 右]`。左上 → 右上 → 右下 → 左下。
            corners: [a.ends[0], a.ends[1], b.ends[1], b.ends[0]],
        };
}
/**
 * 動かしていない境目の位置。**それを含む面**のホモグラフィで、展開図の境目を写す。
 * 面が潰れていて写せなければ `null`。
 */
function projectBoundary(position, anchors, faces, axis, panel) {
    const faceIndex = anchors.findIndex((anchor, i) => position > anchor.position && position < (anchors[i + 1]?.position ?? 0));
    const face = faces[faceIndex];
    if (face === undefined)
        return null;
    let toQuad;
    try {
        toQuad = computeHomography(face.panelCorners, face.corners);
    }
    catch (error) {
        if (error instanceof DegenerateQuadError)
            return null;
        throw error;
    }
    const ends = axis === 'columns'
        ? [apply(toQuad, [position, 0]), apply(toQuad, [position, panel.height])]
        : [apply(toQuad, [0, position]), apply(toQuad, [panel.width, position])];
    return ends.every((point) => point.every(Number.isFinite)) ? ends : null;
}
/**
 * 枠全体の輪郭。**左上から時計回り。**
 *
 * 動かしていない境目は面の辺の上にあるので頂点にならない —— 区切り(外側の辺と動かした境目)の端点だけをつなぐ。
 */
function outlineOf(anchors, axis) {
    const firsts = anchors.map((anchor) => anchor.ends[0]);
    const seconds = anchors.map((anchor) => anchor.ends[1]);
    // 縦の折れ目: 上の端点を左から右へ → 下の端点を右から左へ。
    // 横の折れ目: 右の端点を上から下へ → 左の端点を下から上へ(左上・右上から始める)。
    return axis === 'columns'
        ? [...firsts, ...seconds.reverse()]
        : [firsts[0] ?? [0, 0], ...seconds, ...firsts.slice(1).reverse()];
}
/**
 * **枠の輪郭。** 平面の枠は `quad`、折れた枠は面をつないだ多角形([`foldedLayout`](#foldedLayout) の `outline`)。
 *
 * ドロップ先の内外判定に使う(ビューアの `pickCell` / S04 の `drop-target.ts` —— #340)。
 * 折れた枠は角が外側の 4 隅の線より**張り出したり、へこんだり**するので、4 隅で判定すると外れる。
 *
 * 割り付けが決まらない折れた枠は `quad` に落とす(内外判定を失敗させない)。
 *
 * **曲面の枠**(#361 / [ADR-0060](../../../docs/adr/0060-curved-surface.md))は**見えている部分の輪郭**
 * (表の片の外周 —— 2 つに分かれていれば大きいほう)を**正規化座標**で返す(`quad` は使わない —— 当たり判定と同じく
 * 正規化座標で呼ぶ。[`hit-test.ts`](./hit-test.ts))。**裏に回って何も見えなければ空**(どの点も内側にならない)。
 * 描けない形は `quad` に落とす(折れた枠と同じ)。折れ目と両方あれば曲面を優先する。
 */
export function frameOutline(quad, grid, folds, curve) {
    const curved = { curve };
    if (isCurved(curved)) {
        const layout = curvedLayout(curved.curve, grid);
        return layout === null ? quad : (largestPolygon(curvedOutline(layout).polygons) ?? []);
    }
    if (!isFolded({ folds }))
        return quad;
    return (folds == null ? null : foldedLayout(quad, grid, folds))?.outline ?? quad;
}
/**
 * 折れ目の点を**表示座標**へ。`quad` と同じ変換([`quadToStage`](./layout.ts))を通す ——
 * 割り付けは `quad` と `folds` が同じ座標系であることを前提にしている。
 */
export function foldsToStage(folds, transform) {
    return {
        axis: folds.axis,
        lines: folds.lines.map(({ index, points: [first, second] }) => ({
            index,
            points: [toStage(first, transform), toStage(second, transform)],
        })),
    };
}
/**
 * **面ごとの描画計画。** 表示([`webgl-renderer.ts`](./webgl-renderer.ts))と書き出し([`compose.ts`](./compose.ts))が同じものを使う。
 *
 * **潰れた・ねじれて解けない面は飛ばす**(同意書の仮定 F)—— その面だけ描かず、ほかの面は描く。
 * 編集中に一時的に起こるので、エラーにはしない(平面の枠の潰れたクアッドと同じ扱い)。
 */
export function faceDraws(layout) {
    return layout.faces.flatMap((face) => {
        if (!face.corners.every((corner) => corner.every(Number.isFinite)))
            return [];
        let inverse;
        try {
            inverse = invert(computeHomography(face.panelCorners, face.corners));
        }
        catch (error) {
            if (error instanceof DegenerateQuadError)
                return [];
            throw error;
        }
        return inverse.every(Number.isFinite)
            ? [{ panelCorners: face.panelCorners, corners: face.corners, inverse }]
            : [];
    });
}
//# sourceMappingURL=folds.js.map